import { ToastContext } from "context/ToastContext";
import {
  attachmentUploadPlan,
  modelRunIdOf,
} from "pages/PayerRules/contracts/contractModel";
import {
  CONTRACT_TYPE_OPTIONS,
  modelRunIdempotencyKey,
} from "pages/PayerRules/contracts/helpers";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { RadioButton } from "primereact/radiobutton";
import {
  DragEvent,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  useGetContractFeeSchedulesQuery,
  useUploadAttachmentMutation,
} from "store/queries/contractFeeSchedules";
import {
  DraftSiteTier,
  useConfirmContractModelDraftDocumentMutation,
  useCreateContractModelDraftRunMutation,
  usePresignContractModelDraftDocumentMutation,
  useRunContractModelDraftGenerationMutation,
} from "store/queries/contractModelDrafts";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";

import {
  CONTRACT_DOCUMENTS,
  DROP_FILES_HERE,
  EXISTING_CONTRACT_FILES_HINT,
  EXISTING_CONTRACT_NO_INSTRUCTIONS,
  EXISTING_UPLOAD_RETRY_HINT,
  FILE_STATUS_LABELS,
  INSTRUCTIONS_FOR_MODEL,
  INSTRUCTIONS_PLACEHOLDER,
  MODEL_NOT_STARTED,
  MODEL_NOT_STARTED_DETAIL,
  MODEL_THAT_CONTRACT,
  NEW_CONTRACT_MODEL,
  NEW_MODEL_EXISTING_CONTRACT_HINT,
  NEW_MODEL_FACTS_FIRST_HINT,
  NEW_MODEL_FILES_HINT,
  NEW_MODEL_PROVIDER_HINT,
  OPEN_MODEL,
  OPEN_WITHOUT_STARTING,
  RETRY,
  RETRY_FAILED_UPLOADS,
  START_MODEL_FAILED,
  START_MODELING,
  UPLOAD_CHOOSE_FILES,
  UPLOAD_FAILED,
  UPLOAD_REMOVE_FILE,
  notAddedFiles,
  standingInstructionsStillApply,
  startWithUploaded,
  uploadFailedFiles,
  uploadProgressLine,
} from "./copy";
import {
  INITIAL_PICKED_FILES,
  IPickedFile,
  NEW_MODEL_UPLOAD_CONCURRENCY,
  isRemovable,
  newModelActions,
  pendingFiles,
  pickedFilesReducer,
  uploadProgress,
} from "./newModelFiles";
import {
  INewRunTypeChoice,
  buildRunName,
  buildStandaloneRunRequest,
  canStartModeling,
  existingContractLabel,
  existingContractType,
  needsSiteTier,
  pickExistingContract,
  uploadStartsExistingModel,
} from "./newRun";
import {
  UPLOAD_ACCEPT,
  runWithConcurrency,
  uploadErrorDetail,
  uploadRunDocument,
} from "./uploadDocument";

const SITE_TIER_OPTIONS: { label: string; value: DraftSiteTier }[] = [
  { label: "Office / Non-Facility (POS 11)", value: "non_facility" },
  { label: "Facility / Outpatient (POS 22+)", value: "facility" },
  { label: "Both tiers (one combined schedule)", value: "both" },
];

const TYPE_CHOICES: {
  label: string;
  value: Exclude<INewRunTypeChoice, null>;
}[] = [
  ...(CONTRACT_TYPE_OPTIONS as {
    label: string;
    value: Exclude<INewRunTypeChoice, null | "unsure">;
  }[]),
  { label: "Not sure, let the AI decide", value: "unsure" },
];

/** Where a run opened from this page returns to. */
const RUN_LIST_PATH = "/contract-model-drafts";

const STATUS_ICONS: Record<IPickedFile["status"], string> = {
  queued: "pi pi-file text-700",
  uploading: "pi pi-spin pi-spinner text-700",
  done: "pi pi-check-circle text-green-600",
  failed: "pi pi-exclamation-circle text-red-600",
};

const fileSize = (bytes: number): string => {
  const kb = bytes / 1024;
  return kb >= 1024 ? `${(kb / 1024).toFixed(2)} MB` : `${kb.toFixed(1)} KB`;
};

interface INewContractModelDialogProps {
  visible: boolean;
  onHide: () => void;
}

/**
 * CLE-2811 — "New contract model". Provider, payer and the contract type are required; the rate tier is
 * asked only for a Professional contract. The documents are chosen here too, so "Start modeling" creates
 * the model, uploads them (three at a time) and starts generating in one step — the run page's upload
 * screen is only the fallback for a model left without documents.
 *
 * Once provider and payer are both chosen, an existing contract for the pair is offered: modeling THAT
 * ties the model to the contract (its attachments, and the facts saved back onto it), exactly like
 * "Model with AI" on the contract page. Documents chosen here are attached to that contract first.
 */
export function NewContractModelDialog({
  visible,
  onHide,
}: INewContractModelDialogProps) {
  const navigate = useNavigate();
  const toastRef = useContext(ToastContext);
  const [createRun] = useCreateContractModelDraftRunMutation();
  const [presignDoc] = usePresignContractModelDraftDocumentMutation();
  const [confirmDoc] = useConfirmContractModelDraftDocumentMutation();
  const [runGeneration] = useRunContractModelDraftGenerationMutation();
  const [uploadAttachment] = useUploadAttachmentMutation();
  const { data: providersData } = useGetAllProvidersQuery();
  const { data: payersData } = useGetAllPayersQuery();

  const [providerId, setProviderId] = useState<string | null>(null);
  const [payerId, setPayerId] = useState<number | null>(null);
  const [typeChoice, setTypeChoice] = useState<INewRunTypeChoice>(null);
  const [siteTier, setSiteTier] = useState<DraftSiteTier | null>(null);
  const [instructions, setInstructions] = useState("");
  const [showInstructions, setShowInstructions] = useState(false);
  const [picked, dispatch] = useReducer(
    pickedFilesReducer,
    INITIAL_PICKED_FILES
  );
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // The model "Start modeling" created. Kept so a retry uploads into it — never a second model.
  const [runId, setRunId] = useState<string | null>(null);
  const runIdRef = useRef<string | null>(null);
  // What is in flight: the standalone start (create/upload/generate) or the existing contract's upload.
  const [busyWith, setBusyWith] = useState<"start" | "existing" | null>(null);
  const busyRef = useRef(false);
  // Documents went onto the existing contract: from here on, only "Model that contract" continues.
  const [contractUploadsStarted, setContractUploadsStarted] = useState(false);

  const { files } = picked;
  const busy = busyWith !== null;

  const providers = useMemo(
    () =>
      (providersData?.data ?? []) as {
        id: string;
        name: string;
        specialty: string;
      }[],
    [providersData]
  );
  const payers = useMemo(
    () => (payersData?.data ?? []) as { id: number; name: string }[],
    [payersData]
  );
  const providerOptions = useMemo(
    () =>
      providers
        .map((p) => ({ label: `${p.name} (${p.specialty})`, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [providers]
  );
  const payerOptions = useMemo(
    () =>
      payers
        .map((p) => ({ label: p.name, value: p.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [payers]
  );

  const providerName = providers.find((p) => p.id === providerId)?.name;
  const payerName = payers.find((p) => p.id === payerId)?.name;
  const runName = buildRunName(providerName, payerName);

  // Looked up only once both are chosen: is there already a contract for this provider + payer?
  const { data: contracts, isFetching: lookingUp } =
    useGetContractFeeSchedulesQuery(
      { providerId: providerId ?? undefined, payerId: payerId ?? undefined },
      { skip: !visible || !providerId || payerId == null }
    );
  const existing =
    !lookingUp && providerId && payerId != null
      ? pickExistingContract(contracts, providerId, payerId)
      : null;
  const existingType = existing
    ? existingContractType(typeChoice, existing)
    : undefined;

  const form = { providerId, payerId, typeChoice, siteTier };
  const actions = newModelActions(runId, files);
  const progress = uploadProgress(files);
  // The model exists with these answers, so they can no longer change; documents on the contract pin the pair.
  const formLocked = busy || !!runId;
  const pairLocked = formLocked || contractUploadsStarted;

  const clearAll = () => {
    setProviderId(null);
    setPayerId(null);
    setTypeChoice(null);
    setSiteTier(null);
    setInstructions("");
    setShowInstructions(false);
    dispatch({ type: "reset" });
    setDragOver(false);
    setRunId(null);
    runIdRef.current = null;
    setContractUploadsStarted(false);
    onHide();
  };

  const openRun = (id: string) => {
    clearAll();
    navigate(`${RUN_LIST_PATH}/${id}`, { state: { from: RUN_LIST_PATH } });
  };

  // Closing after the model was created leaves it in the list, not started — say so, with a way back.
  const handleClose = () => {
    if (busyRef.current) return;
    const created = runIdRef.current;
    if (created) {
      toastRef?.current?.show({
        severity: "info",
        summary: MODEL_NOT_STARTED,
        detail: (
          <span>
            {MODEL_NOT_STARTED_DETAIL}{" "}
            <Button
              type="button"
              label={OPEN_MODEL}
              className="p-button-link p-0"
              onClick={() =>
                navigate(`${RUN_LIST_PATH}/${created}`, {
                  state: { from: RUN_LIST_PATH },
                })
              }
            />
          </span>
        ),
        life: 10000,
      });
    }
    clearAll();
  };

  const showError = (e: unknown) => {
    const apiMessage = (e as { data?: { message?: string } })?.data?.message;
    toastRef?.current?.show({
      severity: "error",
      summary: START_MODEL_FAILED,
      detail:
        typeof apiMessage === "string" && apiMessage
          ? apiMessage
          : "Something went wrong. Please try again.",
      life: 6000,
    });
  };

  const begin = (what: "start" | "existing"): boolean => {
    if (busyRef.current) return false;
    busyRef.current = true;
    setBusyWith(what);
    return true;
  };
  const end = () => {
    busyRef.current = false;
    setBusyWith(null);
  };

  /** Queue the model with her instructions and open it. On a refusal the dialog stays, with the model. */
  const generateAndOpen = async (id: string): Promise<void> => {
    try {
      await runGeneration({ runId: id, instructions }).unwrap();
    } catch (e) {
      showError(e);
      return;
    }
    openRun(id);
  };

  /**
   * "Start modeling" (and "Retry failed", and a row's Retry with `only`): create the model once, upload
   * what is not up yet three at a time, and — only when every document is up — generate and open it.
   */
  const handleStart = async (only?: string) => {
    if (!runIdRef.current && !canStartModeling(form, files)) return;
    if (!begin("start")) return;
    try {
      let id = runIdRef.current;
      if (!id) {
        try {
          const run = await createRun(
            buildStandaloneRunRequest(form, runName)
          ).unwrap();
          id = run.id;
        } catch (e) {
          showError(e);
          return;
        }
        runIdRef.current = id;
        setRunId(id);
      }
      const modelId: string = id;
      const targets = pendingFiles(files).filter(
        (f) => only === undefined || f.key === only
      );
      const outcomes = await runWithConcurrency(
        targets,
        NEW_MODEL_UPLOAD_CONCURRENCY,
        async (f) => {
          dispatch({ type: "uploading", key: f.key });
          try {
            await uploadRunDocument(modelId, f.file, {
              presign: presignDoc,
              confirm: confirmDoc,
            });
            dispatch({ type: "done", key: f.key });
          } catch (e) {
            dispatch({
              type: "failed",
              key: f.key,
              error: uploadErrorDetail(e),
            });
            throw e;
          }
        }
      );
      const nowUp = new Set(
        targets.filter((_, i) => outcomes[i].ok).map((f) => f.key)
      );
      const allUp = files.every((f) => f.status === "done" || nowUp.has(f.key));
      // Anything still not up keeps the dialog open: Retry failed / Start with N uploaded / Open.
      if (!allUp) return;
      await generateAndOpen(modelId);
    } finally {
      end();
    }
  };

  /** "Start with N uploaded": generate with what is up and leave the failed files behind. */
  const handleStartWithUploaded = async () => {
    const id = runIdRef.current;
    if (!id || !begin("start")) return;
    try {
      await generateAndOpen(id);
    } finally {
      end();
    }
  };

  // Exactly what the contract page's "Model with AI" sends: provider, payer and documents all come
  // from the contract, and the stage-scoped key returns the facts run already in flight on a retry.
  const createContractRun = async (): Promise<void> => {
    if (!existing || existingType === undefined) return;
    try {
      const run = await createRun({
        contractFeeScheduleId: existing.id,
        contractType: existingType,
        idempotencyKey: modelRunIdempotencyKey(existing.id, "facts"),
        name: runName || undefined,
      }).unwrap();
      openRun(run.id);
    } catch (e) {
      showError(e);
    }
  };

  /**
   * "Model that contract". Documents chosen here go onto the CONTRACT first, one at a time; the last one
   * the AI can read carries `extractWithAi` (when the upload would start the model with her type), so the
   * model starts after everything is attached. That file is held back if an earlier one failed. No model
   * back from the upload — the flag was not sent, or the server started none — creates it as before.
   */
  const handleModelExisting = async () => {
    if (!existing || existingType === undefined || runIdRef.current) return;
    if (!begin("existing")) return;
    try {
      const pending = pendingFiles(files);
      if (pending.length === 0) {
        await createContractRun();
        return;
      }
      setContractUploadsStarted(true);
      const plan = attachmentUploadPlan(
        pending.map((f) => f.file),
        uploadStartsExistingModel(existingType, existing)
      );
      const result = await plan.reduce<
        Promise<{ failed: string[]; modelRunId: string | null }>
      >(async (previous, step) => {
        const sofar = await previous;
        const row = pending.find((f) => f.file === step.file);
        if (!row || (step.extractWithAi && sofar.failed.length > 0))
          return sofar;
        dispatch({ type: "uploading", key: row.key });
        try {
          const uploaded = await uploadAttachment({
            contractId: existing.id,
            file: step.file,
            ...(step.extractWithAi ? { extractWithAi: true } : {}),
          }).unwrap();
          dispatch({ type: "done", key: row.key });
          return {
            ...sofar,
            modelRunId: modelRunIdOf(uploaded) ?? sofar.modelRunId,
          };
        } catch (e) {
          dispatch({
            type: "failed",
            key: row.key,
            error: uploadErrorDetail(e),
          });
          return { ...sofar, failed: [...sofar.failed, step.file.name] };
        }
      }, Promise.resolve({ failed: [], modelRunId: null }));

      if (result.failed.length > 0) {
        toastRef?.current?.show({
          severity: "error",
          summary: UPLOAD_FAILED,
          detail: uploadFailedFiles(result.failed),
          life: 7000,
        });
        return;
      }
      if (result.modelRunId) {
        openRun(result.modelRunId);
        return;
      }
      await createContractRun();
    } finally {
      end();
    }
  };

  const addFiles = (list: FileList | null | undefined) => {
    const chosen = Array.from(list ?? []);
    if (chosen.length) dispatch({ type: "add", files: chosen });
  };

  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (!busy) addFiles(e.dataTransfer?.files);
  };

  const footer = (
    <div className="flex flex-wrap justify-content-end gap-2">
      <Button
        label="Cancel"
        className="p-button-text"
        disabled={busy}
        onClick={handleClose}
      />
      {actions.openWithoutStarting ? (
        <Button
          label={OPEN_WITHOUT_STARTING}
          className="p-button-outlined"
          disabled={busy}
          onClick={() => runId && openRun(runId)}
        />
      ) : null}
      {actions.retryFailed ? (
        <Button
          label={RETRY_FAILED_UPLOADS}
          icon="pi pi-refresh"
          className="p-button-outlined"
          disabled={busy}
          onClick={() => handleStart()}
        />
      ) : null}
      {actions.startWithUploaded !== null ? (
        <Button
          label={startWithUploaded(actions.startWithUploaded)}
          icon="pi pi-bolt"
          disabled={busy}
          onClick={handleStartWithUploaded}
        />
      ) : null}
      {actions.start ? (
        <Button
          label={START_MODELING}
          icon="pi pi-bolt"
          disabled={
            busy || contractUploadsStarted || !canStartModeling(form, files)
          }
          loading={busyWith === "start"}
          onClick={() => handleStart()}
        />
      ) : null}
    </div>
  );

  return (
    <Dialog
      header={NEW_CONTRACT_MODEL}
      visible={visible}
      style={{ width: "40rem", maxWidth: "95vw" }}
      onHide={handleClose}
      closable={!busy}
      closeOnEscape={!busy}
      footer={footer}
    >
      <label className="block mb-1 font-medium" htmlFor="run-provider">
        Provider
      </label>
      <Dropdown
        inputId="run-provider"
        className="w-full"
        value={providerId}
        options={providerOptions}
        filter
        disabled={pairLocked}
        placeholder="Select the provider entity"
        onChange={(e) => setProviderId(e.value)}
      />
      <small className="text-500 block mt-1 mb-3">
        {NEW_MODEL_PROVIDER_HINT}
      </small>

      <label className="block mb-1 font-medium" htmlFor="run-payer">
        Payer
      </label>
      <Dropdown
        inputId="run-payer"
        className="w-full mb-3"
        value={payerId}
        options={payerOptions}
        filter
        disabled={pairLocked}
        placeholder="Select the payer"
        onChange={(e) => setPayerId(e.value)}
      />

      <span className="block mb-2 font-medium">Contract type</span>
      <div className="flex flex-column gap-2 mb-3">
        {TYPE_CHOICES.map((option) => (
          <div key={option.value} className="flex align-items-center gap-2">
            <RadioButton
              inputId={`run-type-${option.value}`}
              name="runContractType"
              checked={typeChoice === option.value}
              disabled={formLocked}
              onChange={() => setTypeChoice(option.value)}
            />
            <label
              htmlFor={`run-type-${option.value}`}
              className="mb-0 cursor-pointer"
            >
              {option.label}
            </label>
          </div>
        ))}
      </div>

      {needsSiteTier(typeChoice) && (
        <>
          <label className="block mb-1 font-medium" htmlFor="run-tier">
            Rate tier
          </label>
          <Dropdown
            inputId="run-tier"
            className="w-full"
            value={siteTier}
            options={SITE_TIER_OPTIONS}
            disabled={formLocked}
            placeholder="Which rate column does this provider bill?"
            onChange={(e) => setSiteTier(e.value)}
          />
          <small className="text-500 block mt-1 mb-3">
            Contracts often list separate office and facility columns; this
            tells the model which one applies to this provider.
          </small>
        </>
      )}

      <span className="block mb-2 font-medium">{CONTRACT_DOCUMENTS}</span>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        accept={UPLOAD_ACCEPT}
        data-testid="new-model-file-input"
        onChange={(e) => {
          addFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <div
        data-testid="new-model-drop-zone"
        className={`border-2 border-dashed border-round p-3 text-center ${
          dragOver ? "border-primary surface-100" : "surface-border"
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!busy) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        <i className="pi pi-cloud-upload text-2xl text-500 block mb-1" />
        <span className="text-700 text-sm">{DROP_FILES_HERE} </span>
        <Button
          type="button"
          label={UPLOAD_CHOOSE_FILES}
          className="p-button-link p-button-sm p-0"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        />
        <small className="text-500 block mt-1">{NEW_MODEL_FILES_HINT}</small>
      </div>
      {picked.rejected.length > 0 ? (
        <small className="text-red-600 block mt-2">
          {notAddedFiles(picked.rejected)}
        </small>
      ) : null}
      {files.length > 0 ? (
        <ul className="list-none p-0 mt-2 mb-0" aria-label={CONTRACT_DOCUMENTS}>
          {files.map((f) => (
            <li key={f.key} className="py-1 text-sm">
              <div className="flex align-items-center gap-2">
                <i className={STATUS_ICONS[f.status]} aria-hidden />
                <span className="flex-1" style={{ overflowWrap: "anywhere" }}>
                  {f.file.name}
                </span>
                <span className="text-600">{fileSize(f.file.size)}</span>
                <span
                  className={
                    f.status === "failed" ? "text-red-600" : "text-600"
                  }
                >
                  {FILE_STATUS_LABELS[f.status]}
                </span>
                {f.status === "failed" && runId ? (
                  <Button
                    type="button"
                    label={RETRY}
                    className="p-button-text p-button-sm"
                    aria-label={`${RETRY} ${f.file.name}`}
                    disabled={busy}
                    onClick={() => handleStart(f.key)}
                  />
                ) : null}
                {isRemovable(f) ? (
                  <Button
                    type="button"
                    icon="pi pi-times"
                    className="p-button-text p-button-sm p-button-danger"
                    aria-label={`${UPLOAD_REMOVE_FILE}: ${f.file.name}`}
                    disabled={busy}
                    onClick={() => dispatch({ type: "remove", key: f.key })}
                  />
                ) : null}
              </div>
              {f.status === "failed" && f.error ? (
                <small className="text-red-600 block ml-4">{f.error}</small>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {progress.started ? (
        <small className="text-600 block mt-1" data-testid="upload-progress">
          {uploadProgressLine(progress.done, progress.total, progress.failed)}
        </small>
      ) : null}

      <div className="mt-3 mb-3">
        <Button
          type="button"
          label={INSTRUCTIONS_FOR_MODEL}
          icon={showInstructions ? "pi pi-chevron-down" : "pi pi-chevron-right"}
          className="p-button-text p-button-sm p-0"
          aria-expanded={showInstructions}
          aria-controls="new-model-instructions"
          onClick={() => setShowInstructions((v) => !v)}
        />
        {showInstructions ? (
          <>
            <InputTextarea
              id="new-model-instructions"
              aria-label={INSTRUCTIONS_FOR_MODEL}
              className="w-full mt-2"
              autoResize
              rows={3}
              disabled={busy}
              value={instructions}
              placeholder={INSTRUCTIONS_PLACEHOLDER}
              onChange={(e) => setInstructions(e.target.value)}
            />
            <small className="text-500 block mt-1">
              {standingInstructionsStillApply(typeChoice)}
            </small>
          </>
        ) : null}
      </div>

      {existing && (
        <div className="p-3 mb-3 border-round surface-100 border-left-3 border-blue-500">
          <div className="flex gap-2">
            <i className="pi pi-info-circle text-blue-500 mt-1" />
            <div>
              <p className="m-0 mb-2">
                A contract already exists for this provider and payer:{" "}
                <strong>
                  {existingContractLabel(existing, runName || "this contract")}
                </strong>
                . {NEW_MODEL_EXISTING_CONTRACT_HINT}
                {files.length > 0 ? ` ${EXISTING_CONTRACT_FILES_HINT}` : ""}
              </p>
              <Button
                label={MODEL_THAT_CONTRACT}
                icon="pi pi-sparkles"
                className="p-button-sm p-button-outlined"
                disabled={busy || existingType === undefined || !!runId}
                loading={busyWith === "existing"}
                onClick={handleModelExisting}
              />
              {existingType === undefined && (
                <small className="text-500 block mt-2">
                  Choose the contract type first &mdash; this contract does not
                  record one.
                </small>
              )}
              {instructions.trim() ? (
                <small className="text-500 block mt-2">
                  {EXISTING_CONTRACT_NO_INSTRUCTIONS}
                </small>
              ) : null}
              {contractUploadsStarted && progress.failed > 0 && !busy ? (
                <small className="text-red-600 block mt-2">
                  {EXISTING_UPLOAD_RETRY_HINT}
                </small>
              ) : null}
            </div>
          </div>
        </div>
      )}

      <small className="text-500 block">{NEW_MODEL_FACTS_FIRST_HINT}</small>
    </Dialog>
  );
}
